import { define, reactive } from '@rehaje/reactive'
import { getIn, keys, recursiveTraverse, values } from '@rehaje/shared'

import { UnknownSchema } from '../constants'
import { FatalMissingContextError, FatalSchemaDefinitionError } from '../errors'
import { TransformEvent } from '../events/Transform'
import { createJSDoc } from '../lambdas/createJSDoc'
import { getPrefixName } from '../lambdas/getPrefixName'
import { DefinitionStore } from '../stores/DefinitionStore'
import { SchemaDefinition } from './SchemaDefinition'

import type { Store } from '../stores/Store'
import type {
  CatchOptions,
  ErrorConstructor,
  InterfaceGenerateOptions,
  RehajePlugin,
  SchemaObjectSpecific,
  TypedDefinitionConstructor,
  TypedSchema
} from '../types'
import type { TypedNode } from './TypedNode'
import type { LintError } from '@rehaje/errors'
import type { Callback } from '@rehaje/shared'

/**
 * 类型定义基类，为任何 ADT 类型提供完备的结构化定义能力
 * 主要用于类型插件继承
 */
export abstract class TypedDefinition {
  static __r_injection_store: Store

  static __r_injection_type: string

  __r_id_cache: Map<string, TypedSchema> = new Map()

  __r_name: string

  __r_path_cache: Map<string, TypedSchema> = new Map()

  __r_store: Store

  /**
   * 是否已被废弃
   */
  deprecated?: boolean

  /**
   * 描述
   */
  description?: string

  /**
   * 示例
   */
  example?: string

  /**
   * 继承类型
   */
  extends: string[] = []

  declare getPlugin: <T extends RehajePlugin>() => T

  /**
   * 属性定义
   */
  properties: TypedSchema[] = []

  /**
   * 自定义 schema 关联类型
   */
  relationTo?: string | string[]

  /**
   * 类型名称
   */
  title?: string

  type = 'Type' as const

  /**
   * 类型定义中心
   */
  get __r_definition(): DefinitionStore {
    return this.__r_store.definition
  }

  get __r_definitions() {
    return this.__r_store.definition.__r_definition_table
  }

  @reactive('computed')
  get __r_schema(): SchemaObjectSpecific {
    return {
      properties: this.properties,
      type: 'Schema.Object'
    }
  }

  /**
   * 子类型集合
   */
  @reactive('computed')
  get children(): TypedDefinition[] {
    const children: TypedDefinition[] = []
    for (const key of keys(this.__r_definitions)) {
      const definition = this.__r_definitions[key]
      if (definition && definition.extends.includes(this.name))
        children.push(definition, ...definition.children)
    }
    return children
  }

  /**
   * 定义类型名称
   */
  get name() {
    return this.__r_name
  }

  /**
   * 父类型集合
   */
  @reactive('computed')
  get parents(): TypedDefinition[] {
    return values(this.__r_definitions).filter((def) =>
      def.children.includes(this)
    )
  }

  /**
   * 状态中心
   */
  get store() {
    return this.__r_store
  }

  constructor() {
    if (!TypedDefinition.__r_injection_store) {
      throw new FatalMissingContextError(
        TypedDefinition.__r_injection_type,
        'Store',
        'createTypedDefinition'
      )
    }
    this.__r_store = TypedDefinition.__r_injection_store
    this.__r_name = TypedDefinition.__r_injection_type
    define(this)
  }

  static __r_createTypedDefinition<T extends TypedDefinitionConstructor>(
    type: string,
    TypedDefinitionClass: T,
    store: Store
  ): InstanceType<T> {
    const prevType = TypedDefinition.__r_injection_type
    const prevStore = TypedDefinition.__r_injection_store
    try {
      TypedDefinition.__r_injection_type = type
      TypedDefinition.__r_injection_store = store
      const definition = new TypedDefinitionClass()
      if (
        definition instanceof SchemaDefinition &&
        definition.name !== 'Schema' &&
        !definition.name.startsWith('Schema.')
      ) {
        throw new FatalSchemaDefinitionError()
      }
      return definition as InstanceType<T>
    } finally {
      TypedDefinition.__r_injection_store = prevStore
      TypedDefinition.__r_injection_type = prevType
    }
  }

  /**
   * 捕获错误
   * @param callback 回调
   * @param options 捕获选项
   */
  catch(callback: Callback, options?: CatchOptions): Error[] {
    const node = this.getCurrentNode()
    return node
      ? node.catch(callback, options)
      : this.__r_store.catch(callback, options)
  }

  /**
   * 修复错误
   * @param constructor 检查错误构造器
   */
  fix<T extends LintError>(constructor: ErrorConstructor<T>): T | undefined {
    const node = this.getCurrentNode()
    return node ? node.fix(constructor) : this.__r_store.fix(constructor)
  }

  /**
   * 获取类型计算当前 TypedNode
   */
  getCurrentNode(): TypedNode | undefined {
    return DefinitionStore.__r_injection_node
  }

  /**
   * 获取类型计算当前对象
   */
  getCurrentParent(): any {
    return DefinitionStore.__r_injection_parent
  }

  /**
   * 获取类型计算当前路径
   */
  getCurrentPath(): PropertyKey[] {
    return DefinitionStore.__r_injection_path ?? []
  }

  /**
   * 基于指定路径获取当前 TypedDefinition 的属性默认值
   * @param path
   */
  getDefaultIn(path: PropertyKey[] = []): any {
    for (let i = path.length; i >= 0; i--) {
      const currentPath = path.slice(0, i)
      const schema = this.getPropertySchemaIn(currentPath) as any
      const value = getIn(schema?.default, path.slice(i))
      if (value !== undefined) {
        return value
      }
    }
    return undefined
  }

  /**
   * 获取节点生产的特定错误信息
   * @param constructor 检查错误构造器
   */
  getLintError<T = LintError>(constructor: ErrorConstructor<T>): T | undefined {
    const node = this.getCurrentNode()
    return node
      ? node.getLintError(constructor)
      : this.__r_store.getLintError(constructor)
  }

  /**
   * 获取节点生产的错误信息
   */
  getLintErrors() {
    const node = this.getCurrentNode()
    return node ? node.getLintErrors() : this.__r_store.getLintErrors()
  }

  /**
   * 基于指定路径获取当前 TypedDefinition 的属性 Schema
   * @param path
   */
  getPropertySchemaIn(path: PropertyKey[] = []): TypedSchema {
    const cacheKey = path.join(',')
    if (this.__r_path_cache.has(cacheKey)) return this.__r_path_cache.get(cacheKey)
    const schema = this.__r_store.definition.getInWithSchema(this.__r_schema, path)
    this.__r_path_cache.set(cacheKey, schema)
    return schema
  }

  /**
   * 通过 id 查询 schema
   * @param id
   */
  getSchemaById(id: string): TypedSchema {
    if (this.__r_id_cache.has(id)) return this.__r_id_cache.get(id)
    let schema: TypedSchema = UnknownSchema
    recursiveTraverse(this.properties, (node) => {
      if (node.id === id && node?.type?.indexOf('Schema.') !== -1) {
        schema = node
        return false
      }
    })
    this.__r_id_cache.set(id, schema)
    return schema
  }

  getTypeScript(options?: InterfaceGenerateOptions) {
    const [name, prefixName] = getPrefixName(this.name)
    const schema = this.__r_schema
    const interfaceName = prefixName
      ? `"${this.name}"`
      : `"${name}" | \`${name}.\${string}\``
    const realSchema = {
      ...schema,
      properties: [
        {
          name: 'type',
          required: true,
          type: 'Schema.Identifier',
          value: interfaceName
        } as TypedSchema
      ].concat((schema.properties ?? []).filter(({ name }) => name !== 'type'))
    }
    const jsdoc = createJSDoc(this)
    const exportString = options?.exported ? 'export ' : ''
    const extendsString = prefixName ? ` extends ${prefixName}` : ''
    const typeScript = this.__r_store.definition.inferTypeScript(realSchema)
    const genericsString = options?.generics?.join(',')
    return `${jsdoc}${exportString}interface ${name}${
      genericsString ? `<${genericsString}>` : ''
    }${extendsString} ${typeScript}`
  }

  /**
   * 按照路径推导 Schema
   * @param path
   * @param value
   */
  inferPropertySchemaIn(path: PropertyKey[] = [], value?: any): TypedSchema {
    const store = this.__r_store.definition
    const schema = this.getPropertySchemaIn(path)
    return store.inferWithSchema(value, schema)
  }

  /**
   * 抛出错误
   * @param error
   */
  raise(error: Error): void {
    const node = this.getCurrentNode()
    if (node) node.raise(error)
    else return this.__r_store.raise(error)
  }

  testPropertyIn(path: PropertyKey[] = [], value: any): boolean {
    return this.validatePropertyIn(path, value).length === 0
  }

  /**
   * 基于指定路径的 Schema 进行类型转换
   *  @param path
   *  @param oldValue 类型推导参考值
   *  @param entry 动态推导入口数据
   */
  transformPropertyIn(path?: PropertyKey[], value?: any): any
  transformPropertyIn(path: PropertyKey[] = [], oldValue: any) {
    const schema = this.getPropertySchemaIn(path)
    // 主动转换一定是严格模式，从父级自上而下的转换支持自定义
    const value = this.__r_store.definition.transformWithSchema(
      oldValue,
      schema,
      true
    )
    if (oldValue === value) return value
    this.getCurrentNode()?.dispatchEvent(
      new TransformEvent({ oldValue, value })
    )
    return value
  }

  validatePropertyIn(path: PropertyKey[] = [], value: any): Error[] {
    const schema = this.getPropertySchemaIn(path)
    return this.__r_store.definition.validateWithSchema(value, schema)
  }
}
